import { useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { json } from "@codemirror/lang-json";
import { linter, lintGutter } from "@codemirror/lint";

const LANGS = { jsx: () => javascript({ jsx: true, typescript: true }), html: () => html(), css: () => css(), js: () => javascript(), json: () => json() };

export async function formatCode(code, lang) {
  const prettier = await import("prettier/standalone");
  const babel = await import("prettier/plugins/babel");
  const estree = await import("prettier/plugins/estree");
  const postcss = await import("prettier/plugins/postcss");
  const htmlP = await import("prettier/plugins/html");
  const parser = { jsx: "babel-ts", js: "babel", html: "html", css: "css", json: "json" }[lang];
  return prettier.format(code, { parser, plugins: [babel, estree, postcss, htmlP], printWidth: 100, singleQuote: true });
}

// check(code) -> Promise<{ok, error, line, column}>; errors become inline diagnostics on the editor
function serverLinter(check) {
  return linter(async (view) => {
    const doc = view.state.doc;
    if (!doc.length) return [];
    const r = await check(doc.toString()).catch(() => null);
    if (!r || r.ok) return [];
    const line = r.line && r.line <= doc.lines ? doc.line(r.line) : null;
    const from = line ? Math.min(line.from + Math.max(0, (r.column || 1) - 1), line.to) : 0;
    const to = line ? line.to : Math.min(doc.length, 1);
    return [{ from, to: Math.max(to, from), severity: "error", message: r.error }];
  }, { delay: 900 });
}

export default function CodeEditor({ value, onChange, lang = "jsx", testId = "code-editor", check }) {
  const extensions = useMemo(() => [LANGS[lang](), ...(check ? [serverLinter(check), lintGutter()] : [])], [lang, check]);
  return (
    <div className="h-full min-h-0 overflow-hidden" data-testid={testId}>
      <CodeMirror value={value} height="100%" style={{ height: "100%" }} extensions={extensions} onChange={onChange}
        basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true, bracketMatching: true, closeBrackets: true, autocompletion: true, indentOnInput: true }} />
    </div>
  );
}
